import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Check } from 'lucide-react';

import PasswordField from '../../components/Forms/Input/PasswordField';

import { useUserProfile } from '../../hooks/useUserProfile';
import useAlerts from '../../hooks/useAlerts';
import { RootState } from '../../store/store';
import { evaluatePassword } from '../../utils/passwordStrength';

const ChangePassword = () => {
  const { handleUpdatePassword } = useUserProfile();
  const { show: showAlert } = useAlerts();

  // El perfil se guarda como el CustomResponse completo: el usuario real está
  // en `.data` (igual que en PersonalInfo). Se contempla también la forma plana.
  const rawProfile = useSelector(
    (state: RootState) => state.userProfile.userProfile as any
  );
  const userId: number | undefined = rawProfile?.data?.id ?? rawProfile?.id;

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // =========================================================
  // Validación derivada (en vivo)
  // =========================================================
  const checks = useMemo(() => {
    const { currentPassword, newPassword, confirmPassword } = formData;
    const strong = evaluatePassword(newPassword).isAcceptable;
    const sameAsCurrent =
      newPassword.length > 0 && newPassword === currentPassword;
    const confirmFilled = confirmPassword.length > 0;
    const passwordsMatch = confirmFilled && newPassword === confirmPassword;
    const confirmMismatch = confirmFilled && newPassword !== confirmPassword;

    return {
      strong,
      sameAsCurrent,
      confirmFilled,
      passwordsMatch,
      confirmMismatch,
      canSubmit:
        currentPassword.length > 0 &&
        strong &&
        !sameAsCurrent &&
        passwordsMatch,
    };
  }, [formData]);

  // Error mostrado en "Nueva contraseña": submit o "igual a la actual" en vivo.
  const newPasswordError =
    errors.newPassword ||
    (checks.sameAsCurrent
      ? 'La nueva contraseña debe ser distinta a la actual.'
      : '');

  // Error mostrado en "Confirmar": en vivo apenas no coinciden.
  const confirmError =
    errors.confirmPassword ||
    (checks.confirmMismatch ? 'Las contraseñas no coinciden.' : '');

  const validate = () => {
    const next: Record<string, string> = {};

    if (!formData.currentPassword) {
      next.currentPassword = 'Ingresa tu contraseña actual.';
    }
    if (!formData.newPassword) {
      next.newPassword = 'Ingresa la nueva contraseña.';
    } else if (!checks.strong) {
      next.newPassword =
        'La contraseña no cumple los requisitos mínimos de seguridad.';
    } else if (checks.sameAsCurrent) {
      next.newPassword = 'La nueva contraseña debe ser distinta a la actual.';
    }
    if (!formData.confirmPassword) {
      next.confirmPassword = 'Confirma la nueva contraseña.';
    } else if (formData.newPassword !== formData.confirmPassword) {
      next.confirmPassword = 'Las contraseñas no coinciden.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (saving) return;
    if (!validate()) return;

    if (!userId) {
      showAlert(
        'error',
        'Sesión no disponible',
        'No se pudo identificar tu usuario. Vuelve a iniciar sesión e inténtalo de nuevo.'
      );
      return;
    }

    setSaving(true);
    try {
      const ok = await handleUpdatePassword(userId, {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      if (ok) {
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setErrors({});
        setSubmitted(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-title-xsm font-semibold text-black dark:text-white">
          Cambiar Contraseña
        </h2>
        <p className="mt-1 text-sm text-body dark:text-bodydark">
          Actualiza tu contraseña de forma segura para proteger tu cuenta.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2">
        <PasswordField
          label="Contraseña Actual"
          name="currentPassword"
          value={formData.currentPassword}
          onChange={handleChange}
          error={errors.currentPassword}
          placeholder="Ingresa tu contraseña actual"
          autoComplete="current-password"
          required
        />

        <PasswordField
          label="Nueva Contraseña"
          name="newPassword"
          value={formData.newPassword}
          onChange={handleChange}
          error={newPasswordError}
          placeholder="Ingresa tu nueva contraseña"
          showStrength
          required
        />

        <div>
          <PasswordField
            label="Confirmar Nueva Contraseña"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={confirmError}
            placeholder="Confirma tu nueva contraseña"
            required
          />
          {!confirmError && checks.passwordsMatch && (
            <p className="-mt-3 mb-4 flex items-center gap-1.5 text-xs text-success">
              <Check className="h-3.5 w-3.5" />
              Las contraseñas coinciden
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 border-t border-stroke pt-5 dark:border-strokedark">
          {submitted && !checks.canSubmit && (
            <p className="text-sm text-danger">
              Revisa los campos marcados antes de guardar.
            </p>
          )}
          <button
            type="submit"
            disabled={saving || !checks.canSubmit}
            className="rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-white shadow-default transition-all duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
